import type { AxiosInstance } from 'axios';
import type { DashboardPeriodKey } from '../utils/dashboardPeriods';

// Core functions: perform HTTP calls only
const getOwnerBusinessInformationCore = async (
  axiosprivate: AxiosInstance,
  auth: { accessToken?: string | null },
) => {
  return axiosprivate.get(`/bp_get_owner_business_information`, {
    headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
  });
};

const updateOwnerBusinessInformationCore = async (
  axiosprivate: AxiosInstance,
  ownerId: string,
  payload: any,
  auth: { accessToken?: string | null },
) => {
  return axiosprivate.put(`/bp_update-location`, JSON.stringify(payload), {
    headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
  });
};

// Delete owner account
const deleteOwnerAccountInformationCore = async (
  axiosprivate: AxiosInstance,
  ownerId: string,
  auth: { accessToken?: string | null },
) => {
  return axiosprivate.delete(`/bp_owner_delete_account_information`, {
    headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
  });
};

// Owner account (personal) profile update
const updateOwnerAccountInformationCore = async (
  axiosprivate: AxiosInstance,
  ownerId: string,
  payload: any,
  auth: { accessToken?: string | null },
) => {
  return axiosprivate.patch(
    `/bp_update_owner_account_information`,
    JSON.stringify(payload),
    {
      headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
    },
  );
};

const getDashboardTotalsCore = async (
  axiosprivate: AxiosInstance,
  ownerId: string,
  startDate: string,
  endDate: string,
  auth: { accessToken?: string | null },
  periodKey?: DashboardPeriodKey,
) => {
  const queryParams = [
    `bp_start_date=${encodeURIComponent(startDate)}`,
    `bp_end_date=${encodeURIComponent(endDate)}`,
    'includePending=false',
    periodKey ? `periodKey=${encodeURIComponent(periodKey)}` : null,
  ]
    .filter(Boolean)
    .join('&');

  return axiosprivate.get(
    `/bp_get_total_sales_and_earnings_report_information/${encodeURIComponent(
      ownerId,
    )}?${queryParams}`,
    {
      headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
    },
  );
};

const getCurrentAndLastMonthSalesCore = async (
  axiosprivate: AxiosInstance,
  ownerId: string,
  auth: { accessToken?: string | null },
) => {
  return axiosprivate.get(
    `/bp_get_current_and_last_month_total_sales_report_information`,
    {
      headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
    },
  );
};

const getWeeklyMostSellingCore = async (
  axiosprivate: AxiosInstance,
  ownerId: string,
  page: number,
  limit: number,
  startDateISO: string,
  endDateISO: string,
  auth: { accessToken?: string | null },
) => {
  return axiosprivate.get(
    `/bp_get_weekly_most_selling_products_report_information?page=${page}&limit=${limit}&bp_start_date=${encodeURIComponent(
      startDateISO,
    )}&bp_end_date=${encodeURIComponent(endDateISO)}`,
    {
      headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
    },
  );
};

const getDailyPaymentsCore = async (
  axiosprivate: AxiosInstance,
  ownerId: string,
  auth: { accessToken?: string | null },
) => {
  return axiosprivate.get(`/bp_daily_payments_received_information`, {
    headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
  });
};

const getPaymentMethodBreakdownCore = async (
  axiosprivate: AxiosInstance,
  ownerId: string,
  startDate: string,
  endDate: string,
  auth: { accessToken?: string | null },
  periodKey?: DashboardPeriodKey,
  includeSeries?: boolean,
  net?: boolean,
) => {
  const queryParams = [
    `bp_start_date=${encodeURIComponent(startDate)}`,
    `bp_end_date=${encodeURIComponent(endDate)}`,
    periodKey ? `periodKey=${encodeURIComponent(periodKey)}` : null,
    includeSeries === true ? 'includeSeries=true' : null,
    net === false ? 'net=false' : null,
  ]
    .filter(Boolean)
    .join('&');

  return axiosprivate.get(
    `/bp_get_payment_method_breakdown/${encodeURIComponent(
      ownerId,
    )}?${queryParams}`,
    {
      headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
    },
  );
};

const getRevenueOverTimeReportCore = async (
  axiosprivate: AxiosInstance,
  ownerId: string,
  startDate: string,
  endDate: string,
  auth: { accessToken?: string | null },
  periodKey?: DashboardPeriodKey,
  includePending?: boolean,
) => {
  const queryParams = [
    `bp_start_date=${encodeURIComponent(startDate)}`,
    `bp_end_date=${encodeURIComponent(endDate)}`,
    periodKey ? `periodKey=${encodeURIComponent(periodKey)}` : null,
    includePending === true ? 'includePending=true' : null,
  ]
    .filter(Boolean)
    .join('&');

  return axiosprivate.get(
    `/bp_get_revenue_over_time_report_information/${encodeURIComponent(
      ownerId,
    )}?${queryParams}`,
    {
      headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
    },
  );
};

const getProductSalesTotalsByDateCore = async (
  axiosprivate: AxiosInstance,
  ownerId: string,
  page: number,
  limit: number,
  startDate: string,
  endDate: string,
  auth: { accessToken?: string | null },
) => {
  return axiosprivate.get(
    `/bp_get_product_sales_totals_by_date/${encodeURIComponent(
      ownerId,
    )}?page=${page}&limit=${limit}&bp_start_date=${encodeURIComponent(
      startDate,
    )}&bp_end_date=${encodeURIComponent(endDate)}`,
    {
      headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
    },
  );
};

const getLastFourWeeksSalesCore = async (
  axiosprivate: AxiosInstance,
  ownerId: string,
  dateTodayISO: string,
  auth: { accessToken?: string | null },
) => {
  return axiosprivate.get(
    `/bp_get_last_four_weeks_sales_report?dateToday=${encodeURIComponent(
      dateTodayISO,
    )}`,
    {
      headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
    },
  );
};

// Public API: minimal logic, keep separate from core
export const getDashboardTotals = async (
  axiosprivate: AxiosInstance,
  ownerId: string,
  startDate: string,
  endDate: string,
  auth: { accessToken?: string | null },
  periodKey?: DashboardPeriodKey,
) => {
  try {
    return await getDashboardTotalsCore(
      axiosprivate,
      ownerId,
      startDate,
      endDate,
      auth,
      periodKey,
    );
  } catch (error) {
    return Promise.reject(error);
  }
};

export const getCurrentAndLastMonthSales = async (
  axiosprivate: AxiosInstance,
  ownerId: string,
  auth: { accessToken?: string | null },
) => {
  try {
    return await getCurrentAndLastMonthSalesCore(axiosprivate, ownerId, auth);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const getWeeklyMostSelling = async (
  axiosprivate: AxiosInstance,
  ownerId: string,
  page: number,
  limit: number,
  startDateISO: string,
  endDateISO: string,
  auth: { accessToken?: string | null },
) => {
  try {
    return await getWeeklyMostSellingCore(
      axiosprivate,
      ownerId,
      page,
      limit,
      startDateISO,
      endDateISO,
      auth,
    );
  } catch (error) {
    return Promise.reject(error);
  }
};

export const getDailyPayments = async (
  axiosprivate: AxiosInstance,
  ownerId: string,
  auth: { accessToken?: string | null },
) => {
  try {
    return await getDailyPaymentsCore(axiosprivate, ownerId, auth);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const getLastFourWeeksSales = async (
  axiosprivate: AxiosInstance,
  ownerId: string,
  dateTodayISO: string,
  auth: { accessToken?: string | null },
) => {
  try {
    return await getLastFourWeeksSalesCore(
      axiosprivate,
      ownerId,
      dateTodayISO,
      auth,
    );
  } catch (error) {
    return Promise.reject(error);
  }
};

export type WeeklySalesPoint = {
  week: string | number;
  currentMonthSales: { sales: number };
  previousMonthSales: { sales: number };
};

// ===================== OWNER BUSINESS PROFILE =====================
export const getOwnerBusinessInformation = async (
  axiosprivate: AxiosInstance,
  auth: { accessToken?: string | null },
) => {
  try {
    return await getOwnerBusinessInformationCore(axiosprivate, auth);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const updateOwnerBusinessInformation = async (
  axiosprivate: AxiosInstance,
  ownerId: string,
  payload: any,
  auth: { accessToken?: string | null },
) => {
  try {
    return await updateOwnerBusinessInformationCore(
      axiosprivate,
      ownerId,
      payload,
      auth,
    );
  } catch (error) {
    return Promise.reject(error);
  }
};

export const updateOwnerAccountInformation = async (
  axiosprivate: AxiosInstance,
  ownerId: string,
  payload: any,
  auth: { accessToken?: string | null },
) => {
  try {
    return await updateOwnerAccountInformationCore(
      axiosprivate,
      ownerId,
      payload,
      auth,
    );
  } catch (error) {
    return Promise.reject(error);
  }
};

/** Account-scoped preferences (e.g. test_location_enabled). PATCH, owner JWT. */
const updateAccountPreferencesCore = async (
  axiosprivate: AxiosInstance,
  payload: { test_location_enabled?: boolean },
  auth: { accessToken?: string | null },
) => {
  return axiosprivate.patch(
    `/bp_update_account_preferences`,
    JSON.stringify(payload),
    {
      headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
    },
  );
};

export const updateAccountPreferences = async (
  axiosprivate: AxiosInstance,
  payload: { test_location_enabled?: boolean },
  auth: { accessToken?: string | null },
) => {
  try {
    return await updateAccountPreferencesCore(axiosprivate, payload, auth);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const deleteOwnerAccountInformation = async (
  axiosprivate: AxiosInstance,
  ownerId: string,
  auth: { accessToken?: string | null },
) => {
  try {
    return await deleteOwnerAccountInformationCore(axiosprivate, ownerId, auth);
  } catch (error) {
    return Promise.reject(error);
  }
};

// ============ SALES PAGE APIs (Monthly, Category, All transactions) ==========

// Core functions
const getMonthlySalesReportCore = async (
  axiosprivate: AxiosInstance,
  ownerId: string,
  startDateISO: string,
  endDateISO: string,
  auth: { accessToken?: string | null },
) => {
  return axiosprivate.get(
    `/bp_get_monthly_sales_report_information?bp_start_date=${encodeURIComponent(
      startDateISO,
    )}&bp_end_date=${encodeURIComponent(endDateISO)}`,
    {
      headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
    },
  );
};

// Year-only: monthly sales aggregated for all products in a given year
const getAllProductsMonthlySalesReportYearCore = async (
  axiosprivate: AxiosInstance,
  ownerId: string,
  year: number | string,
  auth: { accessToken?: string | null },
) => {
  return axiosprivate.get(
    `/bp_get_all_products_monthly_sales_report_information?bp_year=${encodeURIComponent(
      String(year),
    )}`,
    {
      headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
    },
  );
};

const getCategorySalesReportCore = async (
  axiosprivate: AxiosInstance,
  ownerId: string,
  startDateISO: string,
  endDateISO: string,
  auth: { accessToken?: string | null },
) => {
  return axiosprivate.get(
    `/bp_get_category_sales_by_date_report_information?bp_start_date=${encodeURIComponent(
      startDateISO,
    )}&bp_end_date=${encodeURIComponent(endDateISO)}`,
    {
      headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
    },
  );
};

const getAllSalesTransactionsCore = async (
  axiosprivate: AxiosInstance,
  ownerId: string,
  page: number,
  limit: number,
  auth: { accessToken?: string | null },
) => {
  return axiosprivate.get(
    `/bp_get_all_sales_report_information?page=${page}&limit=${limit}`,
    {
      headers: { Authorization: 'Bearer ' + String(auth?.accessToken || '') },
    },
  );
};

// Public wrappers
export const getMonthlySalesReport = async (
  axiosprivate: AxiosInstance,
  ownerId: string,
  startDateISO: string,
  endDateISO: string,
  auth: { accessToken?: string | null },
) => {
  try {
    return await getMonthlySalesReportCore(
      axiosprivate,
      ownerId,
      startDateISO,
      endDateISO,
      auth,
    );
  } catch (error) {
    return Promise.reject(error);
  }
};

export const getAllProductsMonthlySalesReportYear = async (
  axiosprivate: AxiosInstance,
  ownerId: string,
  year: number | string,
  auth: { accessToken?: string | null },
) => {
  try {
    return await getAllProductsMonthlySalesReportYearCore(
      axiosprivate,
      ownerId,
      year,
      auth,
    );
  } catch (error) {
    return Promise.reject(error);
  }
};

export const getCategorySalesReport = async (
  axiosprivate: AxiosInstance,
  ownerId: string,
  startDateISO: string,
  endDateISO: string,
  auth: { accessToken?: string | null },
) => {
  try {
    return await getCategorySalesReportCore(
      axiosprivate,
      ownerId,
      startDateISO,
      endDateISO,
      auth,
    );
  } catch (error) {
    return Promise.reject(error);
  }
};

export const getAllSalesTransactions = async (
  axiosprivate: AxiosInstance,
  ownerId: string,
  page: number,
  limit: number,
  auth: { accessToken?: string | null },
) => {
  try {
    return await getAllSalesTransactionsCore(
      axiosprivate,
      ownerId,
      page,
      limit,
      auth,
    );
  } catch (error) {
    return Promise.reject(error);
  }
};

export const getPaymentMethodBreakdown = async (
  axiosprivate: AxiosInstance,
  ownerId: string,
  startDate: string,
  endDate: string,
  auth: { accessToken?: string | null },
  periodKey?: DashboardPeriodKey,
  includeSeries?: boolean,
  net?: boolean,
) => {
  try {
    return await getPaymentMethodBreakdownCore(
      axiosprivate,
      ownerId,
      startDate,
      endDate,
      auth,
      periodKey,
      includeSeries,
      net,
    );
  } catch (error) {
    return Promise.reject(error);
  }
};

export const getRevenueOverTimeReport = async (
  axiosprivate: AxiosInstance,
  ownerId: string,
  startDate: string,
  endDate: string,
  auth: { accessToken?: string | null },
  periodKey?: DashboardPeriodKey,
  includePending?: boolean,
) => {
  try {
    return await getRevenueOverTimeReportCore(
      axiosprivate,
      ownerId,
      startDate,
      endDate,
      auth,
      periodKey,
      includePending,
    );
  } catch (error) {
    return Promise.reject(error);
  }
};

export const getProductSalesTotalsByDate = async (
  axiosprivate: AxiosInstance,
  ownerId: string,
  page: number,
  limit: number,
  startDate: string,
  endDate: string,
  auth: { accessToken?: string | null },
) => {
  try {
    return await getProductSalesTotalsByDateCore(
      axiosprivate,
      ownerId,
      page,
      limit,
      startDate,
      endDate,
      auth,
    );
  } catch (error) {
    return Promise.reject(error);
  }
};

export type MonthlySalesResponseItem = { name: string; totalSales: number };
export type CategorySalesResponseItem = { name: string; sales: number };
